import React, { useRef, useState, useEffect } from 'react';
import * as cls from './turn-insights-into-action.module.css';
import { Typography } from '../../../ui/components/typography/typography';
import { IMG_PATH } from '../../../constants';
import { ListToggle } from '../../../ui/layouts/list-toggle/list-toggle';

const items = [
  {
    title: 'Know your savings potential in your currency',
    text: (
      <Typography>
        Our platform calculates how much money you could save by adjusting
        habits or eliminating waste. Every insight is translated into real
        pounds and pence.
      </Typography>
    ),
  },
  {
    title: "Compare your home's performance with VoltaScore",
    text: (
      <Typography>
        Understand your energy usage at a glance. Our app shows appliance usage
        breakdowns across your home, making it easy to spot power-hungry
        appliances and energy-sapping behaviours.
      </Typography>
    ),
  },
  {
    title: 'Get tailored recommendations',
    text: (
      <>
        <Typography>
          <strong>Get tailored recommendations</strong>
        </Typography>
        <Typography>
          Based on your usage patterns, Voltaware suggests:
        </Typography>
        <ul className={cls.list}>
          <li>
            <Typography>
              <b>Light home upgrades</b> to reduce waste (like switching to LEDs
              or adding smart plugs).
            </Typography>
          </li>
          <li>
            <Typography>
              <b>Better-fit energy tariffs</b>, matched to your real usage to
              avoid overpaying. <span className={cls.badge}>Coming soon</span>
            </Typography>
          </li>
        </ul>
      </>
    ),
  },
];

export const TurnInsightsIntoAction = () => {
  const sectionRef = useRef(null);
  const [animatedItems, setAnimatedItems] = useState(new Set());
  const [imageAnimated, setImageAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const listItems = sectionRef.current?.querySelectorAll(
            `.${cls.wrapper} > div:first-child > div > div`,
          );
          if (listItems) {
            listItems.forEach((item, index) => {
              setTimeout(() => {
                setAnimatedItems((prev) => new Set(prev).add(index));
              }, index * 400);
            });
          }

          setTimeout(() => {
            setImageAnimated(true);
          }, 1200);
        }
      },
      {
        threshold: 0.4,
        rootMargin: '-50px 0px',
      },
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  return (
    <div ref={sectionRef}>
      <Typography className={cls.title} tag="h2">
        Turn Insights into Action
      </Typography>

      <Typography style={{ maxWidth: 850 }}>
        Voltaware doesn’t just show you where energy is going — it helps you use
        that insight. Based on your habits, the app can recommend:
      </Typography>

      <div className={cls.wrapper}>
        <div>
          <div>
            <ListToggle
              animatedItems={animatedItems}
              classNameActive={cls.itemActive}
              classNameAnimated={cls.animated}
              classNameHidden={cls.hiddenLeft}
              items={items}
            />
          </div>
          <Typography className={cls.everyInsight}>
            <b>Every insight is an opportunity to save.</b>
          </Typography>
        </div>
        <img
          src={`${IMG_PATH}/home/savings-insights.png`}
          alt="savings insights in voltaware mobile app"
          className={imageAnimated ? cls.animatedImage : cls.hiddenImage}
        />
      </div>
    </div>
  );
};
